import { useSystemStore, useAuthStore } from '@/stores'
import { toast } from '@/common/util'
import baseApi from './base.api'

import type {
  ApiOkResult,
  ApiOkPagedResult,
  CompetitionDto,
  CompetitionEventDto,
  CompetitionParticipantDto,
  CreateBonusScoreResultDto,
  DomainEventDto,
  GamificationAuditDto,
  MissionContentDto,
  MissionDto,
  MissionProgressDto,
  PillarDto,
  PillarWithDomainEventsDto,
  ScoreDto,
  TeamDto,
  CreateUpdateCompetitionDto,
  RequestRecalculationDto,
  BatchUpsertCompetitionEventsDto,
  CreateCompetitionParticipantDto,
  UpdateCompetitionParticipantDto,
  DeactivateCompetitionParticipantDto,
  TransferCompetitionParticipantDto,
  CreateDomainEventDto,
  UpdateDomainEventDto,
  CreateMissionDto,
  UpdateMissionDto,
  CreateMissionContentDto,
  UpdateMissionContentDto,
  CreateMissionProgressDto,
  UpdateMissionProgressDto,
  CreateBonusScoreDto,
  CreateReversalScoreDto,
  CreateTeamDto,
  UpdateTeamDto,
  GetCompetitionsParams,
  GetCompetitionEventsParams,
  GetCompetitionParticipantsParams,
  GetRankingParams,
  GetDomainEventsParams,
  GetAuditsParams,
  GetMissionsParams,
  GetMissionContentsParams,
  GetMissionProgressesParams,
  GetPillarsParams,
  GetScoresParams,
  GetScoreBalanceParams,
  GetTeamsParams,
  MissionContentTypeEnum
} from '@/services/types/game'

interface PointsRequestDto {
  id: string
  competitionId: string
  userId: string
  domainEventId: string
  entityType: MissionContentTypeEnum
  entityId: string | null
  attachmentUrl: string | null
  description: string | null
  status: 'Pending' | 'Approved' | 'Rejected'
  reviewedByUserId: string | null
  reviewNotes: string | null
  reviewedAt: string | null
  active: boolean
  createdAt: string
}

export type * from '@/services/types/game'

const getJson = (str: string) => {
  try {
    return JSON.parse(str)
  } catch {
    return false
  }
}

const getHeaders = (): Record<string, string> => {
  const authStore = useAuthStore()
  const systemStore = useSystemStore()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': systemStore.clientSettings?.lang || 'pt'
  }

  if (authStore.loggedIn && authStore.token) {
    headers['Authorization'] = `${authStore.token.token_type} ${authStore.token.access_token}`
    headers['X-Tenant'] = authStore.getActiveTentant
  }

  return headers
}

const getBaseUrl = (): string => {
  const { plantar } = (<any>window).configuration || { plantar: {} }
  return `${plantar?.host || ''}/api/gamification/v1`
}

const fetchApi = async <T = any>(endpoint: string, options: any = {}): Promise<T> => {
  let url = `${getBaseUrl()}${endpoint}`

  if (options.body && typeof options.body === 'object') {
    options.body = JSON.stringify(options.body)
  }

  if (options.params) {
    url += buildQuery(options.params)
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getHeaders(),
      ...options.headers
    }
  })

  if (!response.ok) {
    let errorData: any = await response.text()
    try {
      errorData = JSON.parse(errorData)
    } catch (e) {
      //
    }

    const { details, notifications, success } = errorData || {}
    let errors: string[] = []
      ; ((typeof details === 'string' ? getJson(details)?.notifications || [details] : details) || [])?.forEach((msg: string) => errors.push(msg))
      ; ((typeof notifications === 'string' ? getJson(notifications) || [notifications] : notifications) || [])?.forEach((msg: string) => errors.push(msg))

    if (!success && errors.length > 0) {
      errors?.forEach((msg: string) => {
        toast.error({ message: msg })
      })
    }

    throw { response: { data: errorData, status: response.status } }
  }

  if (response.status === 204) {
    return null as any
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }

  return response.text() as any
}

const buildQuery = (params?: Record<string, any>): string => {
  if (!params) return ''

  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })

  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

const $POST = <T = any>(endpoint: string, options: any = {}) => fetchApi<T>(endpoint, { method: 'POST', ...options })
const $GET = <T = any>(endpoint: string, options: any = {}) => fetchApi<T>(endpoint, { method: 'GET', ...options })
const $PATCH = <T = any>(endpoint: string, options: any = {}) => fetchApi<T>(endpoint, { method: 'PATCH', ...options })
const $PUT = <T = any>(endpoint: string, options: any = {}) => fetchApi<T>(endpoint, { method: 'PUT', ...options })
const $DELETE = <T = any>(endpoint: string, options: any = {}) => fetchApi<T>(endpoint, { method: 'DELETE', ...options })

export const gameService = {
  competitions: {
    create: (data: CreateUpdateCompetitionDto) => $POST<ApiOkResult<CompetitionDto>>('/competitions', { body: data }),
    update: (id: string, data: CreateUpdateCompetitionDto) => $PATCH<ApiOkResult<CompetitionDto>>(`/competitions/${id}`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/competitions/${id}`),
    getById: (id: string) => $GET<ApiOkResult<CompetitionDto>>(`/competitions/${id}`),
    getAll: (params?: GetCompetitionsParams) => $GET<ApiOkPagedResult<CompetitionDto>>(`/competitions`, { params }),
    close: (id: string) => $POST<ApiOkResult<boolean>>(`/competitions/${id}/close`),
    recalculation: (id: string, data?: RequestRecalculationDto) => $POST<ApiOkResult<boolean>>(`/competitions/${id}/recalculation`, { body: data ?? {} }),
    retroactiveScoring: (id: string) => $POST<ApiOkResult<boolean>>(`/competitions/${id}/retroactive-scoring`),
    //
    updateCover: (id: string, file: File) => {
      const formData = new FormData()
      formData.append('arquivo', file)

      return baseApi.post(`/gamification/competitions/${id}/cover`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    }
  },

  competitionEvents: {
    getAll: (params?: GetCompetitionEventsParams) => $GET<ApiOkResult<CompetitionEventDto[]>>(`/competitionEvents`, { params }),
    batchUpsert: (data: BatchUpsertCompetitionEventsDto) => $POST<ApiOkResult<CompetitionEventDto[]>>(`/competitionEvents/batch`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/competitionEvents/${id}`)
  },

  competitionParticipants: {
    create: (data: CreateCompetitionParticipantDto) => $POST<ApiOkResult<CompetitionParticipantDto>>('/competitionParticipants', { body: data }),
    update: (id: string, data: UpdateCompetitionParticipantDto) => $PATCH<ApiOkResult<CompetitionParticipantDto>>(`/competitionParticipants/${id}`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/competitionParticipants/${id}`),
    getById: (id: string) => $GET<ApiOkResult<CompetitionParticipantDto>>(`/competitionParticipants/${id}`),
    getAll: (params?: GetCompetitionParticipantsParams) => $GET<ApiOkPagedResult<CompetitionParticipantDto>>(`/competitionParticipants`, { params }),
    deactivate: (id: string, data?: DeactivateCompetitionParticipantDto) => $POST<ApiOkResult<boolean>>(`/competitionParticipants/${id}/deactivate`, { body: data ?? {} }),
    activate: (id: string) => $POST<ApiOkResult<boolean>>(`/competitionParticipants/${id}/activate`),
    transfer: (data: TransferCompetitionParticipantDto) => $POST<ApiOkResult<CompetitionParticipantDto>>(`/competitionParticipants/transfer`, { body: data }),
    getRanking: (params?: GetRankingParams) => $GET<ApiOkPagedResult<CompetitionParticipantDto>>(`/competitionParticipants/ranking`, { params }),

    //
    users: (competitionId: string) => $GET<ApiOkResult<{
      participantId?: string
      userId: string
      teamId?: string
      provisionado?: string
    }[]>>(`/competitionParticipants/${competitionId}/users`)
  },

  domainEvents: {
    create: (data: CreateDomainEventDto) => $POST<ApiOkResult<DomainEventDto>>(`/domainEvents`, { body: data }),
    update: (id: string, data: UpdateDomainEventDto) => $PUT<ApiOkResult<DomainEventDto>>(`/domainEvents/${id}`, { body: data }),
    getById: (id: string) => $GET<ApiOkResult<DomainEventDto>>(`/domainEvents/${id}`),
    getAll: (params?: GetDomainEventsParams) => $GET<ApiOkResult<DomainEventDto[]>>(`/domainEvents`, { params }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/domainEvents/${id}`)
  },

  gamificationAudits: {
    getAll: (params?: GetAuditsParams) => {
      return $GET(`/gamificationAudits`, { params })
        .then((res: ApiOkPagedResult<GamificationAuditDto>) => {
          return {
            ...res,
            data: res.data?.map((audit) => {
              return {
                ...audit,
                dataAfter: typeof audit.dataAfter === 'string' ? getJson(audit.dataAfter) : audit.dataAfter
              }
            })
          }
        })
    },
    getById: (id: string) => $GET<ApiOkResult<GamificationAuditDto>>(`/gamificationAudits/${id}`)
  },

  missions: {
    create: (data: CreateMissionDto) => $POST<ApiOkResult<MissionDto>>('/missions', { body: data }),
    update: (id: string, data: UpdateMissionDto) => $PUT<ApiOkResult<MissionDto>>(`/missions/${id}`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/missions/${id}`),
    getById: (id: string) => $GET<ApiOkResult<MissionDto>>(`/missions/${id}`),
    getAll: (params?: GetMissionsParams) => $GET<ApiOkResult<MissionDto[]>>(`/missions`, { params }),
    patch: (id: string, params: {
      name: string, description: string, frequency?: string, startDate?: string, endDate?: string, missionContents?: {
        id?: string
        entityType: MissionContentTypeEnum
        entityId: string
      }[]
    }) => $PATCH<ApiOkResult<MissionDto>>(`/missions/${id}`, { body: params }),

    postBatch: (missions: any[]) => $POST<ApiOkResult<any>>(`/missions/batch`, { body: missions }),
    patchBatch: (data: {
      missions: {
        id: string
        name?: string
        description?: string
        frequency?: string
        startDate?: string | null
        endDate?: string | null
        missionContents?: {
          entityType: string
          entityId: string
        }[]
      }[]
    }) => $PATCH<ApiOkResult<MissionDto[]>>(`/missions/batch`, { body: data })
  },

  missionContents: {
    create: (data: CreateMissionContentDto) => $POST<ApiOkResult<MissionContentDto>>('/missionContents', { body: data }),
    update: (id: string, data: UpdateMissionContentDto) => $PUT<ApiOkResult<MissionContentDto>>(`/missionContents/${id}`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/missionContents/${id}`),
    getById: (id: string) => $GET<ApiOkResult<MissionContentDto>>(`/missionContents/${id}`),
    getAll: (params?: GetMissionContentsParams) => $GET<ApiOkResult<MissionContentDto[]>>(`/missionContents`, { params })
  },

  missionProgresses: {
    create: (data: CreateMissionProgressDto) => $POST<ApiOkResult<MissionProgressDto>>('/missionProgresses', { body: data }),
    update: (id: string, data: UpdateMissionProgressDto) => $PUT<ApiOkResult<MissionProgressDto>>(`/missionProgresses/${id}`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/missionProgresses/${id}`),
    getById: (id: string) => $GET<ApiOkResult<MissionProgressDto>>(`/missionProgresses/${id}`),
    getAll: (params?: GetMissionProgressesParams) => $GET<ApiOkResult<MissionProgressDto[]>>(`/missionProgresses`, { params })
  },

  pillars: {
    getById: (id: string) => $GET<ApiOkResult<PillarDto>>(`/pillars/${id}`),
    getAll: (params?: GetPillarsParams) => $GET<ApiOkResult<PillarDto[]>>(`/pillars`, { params }),
    getWithDomainEvents: (competitionId?: string) => $GET<ApiOkResult<PillarWithDomainEventsDto[]>>(`/pillars/withDomainEvents`, { params: { competitionId } })
  },

  pointsRequest: {
    admin: {
      getAll: (params?: any) => $GET<ApiOkResult<PointsRequestDto[]>>('/pointsRequests/admin', { params })
        .then(res => res.data)
        .catch(e => e),
      count: (params?: any) => $GET<ApiOkResult<{
        total: number
        pending: number
        approved: number
        rejected: number
      }>>('/pointsRequests/admin/count', { params })
        .then(res => res.data)
        .catch(e => e)
    },
    approve: (id: string) => $POST<ApiOkResult<PointsRequestDto>>(`/pointsRequests/${id}/approval`),
    reject: (id: string, { reviewNotes }: { reviewNotes: string }) => $POST<ApiOkResult<PointsRequestDto>>(`/pointsRequests/${id}/rejection`, { body: { reviewNotes } })
  },

  scores: {
    getById: (id: string) => $GET<ApiOkResult<ScoreDto>>(`/scores/${id}`),
    getAll: (params?: GetScoresParams) => $GET<ApiOkResult<ScoreDto[]>>(`/scores`, { params }),
    getBalance: (params?: GetScoreBalanceParams) => $GET<ApiOkResult<number>>(`/scores/balance`, { params }),
    grantBonus: (data: CreateBonusScoreDto) => $POST<ApiOkResult<CreateBonusScoreResultDto>>(`/scores/bonus`, { body: data }),
    reversal: (data: CreateReversalScoreDto) => $POST<ApiOkResult<ScoreDto>>(`/scores/reversal`, { body: data }),

    //
    addTeamBonus: ({
      competitionId,
      teamId,
      justification,
      value,
      userId
    }: {
      competitionId: string
      teamId: string | string[]
      justification: string
      value: number
      userId?: string
    }) => $POST<ApiOkResult<any>>(`/scores/team-bonus`, {
      body: {
        competitionId,
        teamIds: typeof teamId === 'string' ? [teamId] : teamId,
        justification,
        value,
        userId
      }
    }),
    getTeamBonus: ({
      competitionId,
      teamId
    }: {
      competitionId: string
      teamId: string
    }) => $GET<ApiOkResult<{
      id: string
      value: number
      justification: string
      userId: string | null
      createdByUserId: string
      createdByUserName: string
      createdAt: string
    }[]>>(`/scores/team-bonus`, { params: { competitionId, teamId } })
  },

  teams: {
    create: (data: CreateTeamDto) => $POST<ApiOkResult<TeamDto>>('/teams', { body: data }),
    update: (id: string, data: UpdateTeamDto) => $PATCH<ApiOkResult<TeamDto>>(`/teams/${id}`, { body: data }),
    delete: (id: string) => $DELETE<ApiOkResult<boolean>>(`/teams/${id}`),
    getById: (id: string) => $GET<ApiOkResult<TeamDto>>(`/teams/${id}`),
    getAll: (params?: GetTeamsParams) => $GET<ApiOkPagedResult<TeamDto>>(`/teams`, { params }),

    // Insere multiplos times, o que pode incluir seus participantes
    postBatch: (competitionId: string, teams: {
      name: string
      description: string | null
      formationType: 'Manual' | 'Automatic'
      participants: {
        userId: string
        displayName: string | null
        avatarUrl: string | null
        roleId: string | null
        departmentId: string | null
        unitId: string | null
      }[]
    }[]) => $POST<ApiOkResult<TeamDto[]>>(`/teams/batch`, {
      body: {
        competitionId,
        teams
      }
    }),

    patchBatch: (competitionId: string, teams: {
      id: string
      name?: string
      formationType?: 'Manual' | 'Automatic'
      description?: string
      configurations?: {
        id?: string
        teamsId?: string
        roleIds?: string
        unitIds?: string
        departmentIds?: string
      }
      participantsToAdd?: {
        userId: string
        roleId?: string
        departmentId?: string
        unitId?: string
      }[]
    }, participantsToRemove?: { userId: string }[]) => {
      return $PATCH<{
        participantsAdded: number
        participantsTransferred: number
        participantsRemoved: number
      }>(`/teams/batch`, {
        body: {
          reason: 'update',
          competitionId,
          teamsToEdit: teams,
          participantsToRemove
        }
      })
    }
  }
}

export default gameService
